import { cn } from "@/lib/utils";

/**
 * One block of a settings page: what it is about on the left, the fields on the
 * right from `lg`, stacked below that.
 *
 * Settings pages titled their groups some fifteen ways (an `h3 text-lg`, a
 * CardTitle, a bordered `SectionHeading`…). The side-by-side layout is the one
 * that scales: the explanation stays next to the fields it explains however long
 * the form gets. Consecutive sections are separated by a rule, so a page is a
 * plain list of them — no Card around each — and the first of its parent
 * drops the rule.
 *
 * `stacked` keeps the heading above the fields at every width, for a section
 * that sits in a column already narrow (a form beside a live preview).
 */
export function SettingsSection({
  title,
  description,
  actions,
  stacked = false,
  tone = "default",
  className,
  children,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Under the description: a link to the docs, a secondary button. */
  actions?: React.ReactNode;
  stacked?: boolean;
  /** `danger` for the one section whose action cannot be undone. */
  tone?: "default" | "danger";
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <section
      data-slot="settings-section"
      className={cn(
        "mt-8 grid gap-x-10 gap-y-4 border-t pt-8 first:mt-0 first:border-t-0 first:pt-0",
        !stacked && "lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]",
        className,
      )}
    >
      <div className="space-y-1">
        <h3
          className={cn(
            "text-base font-semibold",
            tone === "danger" && "text-danger",
          )}
        >
          {title}
        </h3>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
        {actions && <div className="pt-2">{actions}</div>}
      </div>
      <div className="min-w-0 space-y-4">{children}</div>
    </section>
  );
}

